export enum FormElementType {
  TEXT_INPUT = 'text-input',
  RADIO_BUTTON = 'radio-button',
  CHECKBOX = 'checkbox',
  SIGNATURE = 'signature',
  DIGITAL_SIGNATURE = 'digital-signature',
  BIOMETRIC_SIGNATURE = 'biometric-signature',
  INITIALS = 'initials',
  TEXT = 'text',
  ATTACHMENTS_LIST = 'file-list'
}

/**
 * Signer interface for managing form element ownership
 * Each signer has a unique ID, display information, and assigned color
 */
export interface Signer {
  /** Unique identifier for the signer */
  id: string;
  /** Display name of the signer */
  name: string;
  /** Email address of the signer */
  email: string;
  /** Assigned color for visual distinction (pastel color from palette) */
  color: string;
  /** Optional signing-method badge label (resolved/translated by the host app) */
  signMethodLabel?: string;
  /** Optional badge text color (host app supplies the method accent) */
  signMethodColor?: string;
  /** Optional badge background color (host app supplies the method tint) */
  signMethodBgColor?: string;
}

/**
 * 50 carefully selected contrasting colors for signer assignments
 * Colors are arranged to provide maximum visual contrast between consecutive assignments
 * First color is blue-based as requested, followed by high-contrast alternating hues
 */
export const PASTEL_COLORS: readonly string[] = [
  // Primary Blue (First signer - blue based)
  '#4A90E2',

  // High contrast alternating colors for maximum distinction
  '#FF6B6B', // Coral Red
  '#4ECDC4', // Teal
  '#45B7D1', // Sky Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint Green
  '#F7DC6F', // Soft Yellow
  '#BB8FCE', // Lavender Purple
  '#85C1E9', // Light Blue
  '#F1948A', // Pink

  // Secondary tier with good contrast
  '#52C4A5', // Sea Green
  '#FFB347', // Peach
  '#9B59B6', // Purple
  '#3498DB', // Blue
  '#E74C3C', // Red
  '#1ABC9C', // Turquoise
  '#F39C12', // Orange
  '#8E44AD', // Dark Purple
  '#2ECC71', // Green
  '#E67E22', // Dark Orange

  // Tertiary colors for additional signers
  '#95A5A6', // Gray Blue
  '#F8C471', // Light Orange
  '#AED6F1', // Pale Blue
  '#F5B7B1', // Light Pink
  '#A9DFBF', // Light Green
  '#D2B4DE', // Light Purple
  '#F9E79F', // Light Yellow
  '#85DDDE', // Light Cyan
  '#FADBD8', // Very Light Pink
  '#D5DBDB', // Light Gray

  // Additional contrast colors
  '#7FB3D3', // Medium Blue
  '#F8D7DA', // Blush Pink
  '#D1F2EB', // Mint
  '#FCF3CF', // Cream Yellow
  '#EBDEF0', // Lilac
  '#E8F8F5', // Very Light Teal
  '#FEF9E7', // Ivory
  '#EAEDED', // Silver
  '#FDF2E9', // Champagne
  '#EBF5FB', // Ice Blue

  // Extended palette for high-volume use
  '#FDEAEA', // Barely Pink
  '#E8F6F3', // Mint Cream
  '#FEF5E7', // Vanilla
  '#F4F6F7', // Cloud White
  '#EBEDEF', // Light Steel
  '#FDF4F4', // Rose White
  '#E9F7EF', // Light Mint
  '#FEF9E6', // Light Cream
  '#F8F9FA', // Off White
  '#F0F3F4'  // Platinum
] as const;

/**
 * PDF position in exact coordinates (immutable, device-independent)
 * These coordinates are stored and never change with zoom/rotation
 * All values are in PDF points (1 point = 1/72 inch)
 * Origin at bottom-left corner of PDF page (PDF standard)
 */
export interface PDFPosition {
  /** X coordinate in PDF points */
  x: number;
  /** Y coordinate in PDF points - PDF bottom-left origin */
  y: number;
  /** Width in PDF points */
  width: number;
  /** Height in PDF points */
  height: number;
  /** Rotation angle in degrees (optional) */
  angle?: number;
}

/**
 * Screen position in pixel coordinates (calculated, device-dependent)
 * These coordinates are computed from PDF coordinates + viewport
 * Changes with zoom, rotation, device DPI, viewport size
 * Origin at top-left corner of browser viewport
 */
export interface ScreenPosition {
  /** X coordinate in screen pixels */
  x: number;
  /** Y coordinate in screen pixels */
  y: number;
  /** Width in screen pixels */
  width: number;
  /** Height in screen pixels */
  height: number;
  /** Rotation angle in degrees (optional) */
  angle?: number;
  /** Scale factors for display (optional) */
  scaleX?: number;
  scaleY?: number;
}

/**
 * @deprecated Use PDFPosition and ScreenPosition instead
 * Legacy position interface for backward compatibility
 */
export interface FormElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  angle?: number;
  scaleX?: number;
  scaleY?: number;
}

export interface BaseFormElement {
  id: string;
  type: FormElementType;
  pageIndex: number;
  name?: string;
  isReadOnly?: boolean;

  /**
   * ID of the signer who owns this form element
   * Required for all elements - determines ownership and access rights
   */
  signerId: string;

  /**
   * PDF coordinates (STORED) - immutable, device-independent
   * Source of truth for element positioning
   * Never changes with zoom/rotation/viewport
   */
  pdfPosition: PDFPosition;

  /**
   * PDF page dimensions in points for this element
   * Used to calculate proportional coordinates when needed
   */
  pageDimensions: {
    /** Page width in PDF points (e.g., 612 for US Letter) */
    width: number;
    /** Page height in PDF points (e.g., 792 for US Letter) */
    height: number;
  };

  /**
   * Screen coordinates (COMPUTED) - calculated from pdfPosition + viewport
   * Used for Fabric.js positioning and display
   * Recalculated on zoom/rotation/viewport changes
   */
  screenPosition?: ScreenPosition;

  /**
   * @deprecated Use pdfPosition instead
   * Legacy position for backward compatibility during migration
   */
  position?: FormElementPosition;

  properties: Record<string, any>;
  styles?: Record<string, any>;
}

export interface TextInputElement extends BaseFormElement {
  type: FormElementType.TEXT_INPUT;
  properties: {
    placeholder?: string;
    defaultValue?: string;
    required?: boolean;
    maxLength?: number;
    fontSize?: number;
    fontFamily?: string;
    textColor?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
  };
}

export interface RadioButtonElement extends BaseFormElement {
  type: FormElementType.RADIO_BUTTON;
  properties: {
    selectedValue: string;
    label?: string;
    checked?: boolean;
    required?: boolean;
    fontSize?: number;
    fontFamily?: string;
    textColor?: string;
    buttonColor?: string;
  };
}

export interface CheckboxElement extends BaseFormElement {
  type: FormElementType.CHECKBOX;
  properties: {
    label?: string;
    checked?: boolean;
    required?: boolean;
    fontSize?: number;
    fontFamily?: string;
    textColor?: string;
    checkColor?: string;
  };
}

export interface SignatureElement extends BaseFormElement {
  type: FormElementType.SIGNATURE;
  properties: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    required?: boolean;
    signatureData?: string;
  };
}

export interface DigitalSignatureElement extends BaseFormElement {
  type: FormElementType.DIGITAL_SIGNATURE;
  properties: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    required?: boolean;
    signatureData?: string;
    certificateInfo?: string;
    timestampRequired?: boolean;
    signatureMethod?: 'PKI' | 'Adobe' | 'DocuSign';
  };
}

export interface BiometricSignatureElement extends BaseFormElement {
  type: FormElementType.BIOMETRIC_SIGNATURE;
  properties: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    required?: boolean;
    signatureData?: string;
    biometricData?: string;
    pressureSensitive?: boolean;
    velocityTracking?: boolean;
    deviceInfo?: string;
  };
}

export interface InitialsElement extends BaseFormElement {
  type: FormElementType.INITIALS;
  properties: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    required?: boolean;
    signatureData?: string;
  };
}

export interface TextElement extends BaseFormElement {
  type: FormElementType.TEXT;
  properties: {
    text: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: string;
    textColor?: string;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
    italic?: boolean;
    underline?: boolean;
    transparency?: number; // 0 (opaque) to 1 (transparent)
    angle?: number; // rotation angle in degrees

  };
}

export interface AttachmentsListElement extends BaseFormElement {
  type: FormElementType.ATTACHMENTS_LIST;
  properties: {
    // Font properties for backend text rendering
    fontSize?: number;              // 6-18 points (default: 10)
    fontFamily?: string;            // Default: 'Arial'
  };
}

export type FormElement = TextInputElement | RadioButtonElement | CheckboxElement | SignatureElement | DigitalSignatureElement | BiometricSignatureElement | InitialsElement | TextElement | AttachmentsListElement;

export interface FormConfiguration {
  /** Configuration version - v3.0.0 uses exact coordinates with page dimensions */
  version: '3.0.0';
  /** List of signers who can own form elements */
  signers: Signer[];
  /** ID of the currently active signer */
  activeSignerId: string;
  /** Form elements with required signer ownership */
  elements: FormElement[];
  /** Number of pages in the PDF document */
  pageCount: number;
  /** Additional metadata about the configuration */
  metadata?: {
    createdAt: string;
    updatedAt: string;
    author?: string;
    /** Coordinate system used for element positions */
    coordinateSystem: 'PDF_EXACT_POINTS';
  };
}

export interface ToolbarOptions {
  /** Enable/disable the toolbar completely */
  enabled?: boolean;
  /** Use external container for toolbar instead of internal positioning */
  external?: boolean;
  /** ID of the container element where toolbar should be placed (when external=true) */
  containerId?: string;
  /** Show element type labels on buttons */
  showLabels?: boolean;
  /** Show icons on buttons */
  showIcons?: boolean;
  /** Show selected element info section */
  showSelectedInfo?: boolean;
  /** Show action buttons (clear all, etc.) */
  showActions?: boolean;
  /** Custom CSS classes to apply to toolbar */
  customClasses?: string[];
  /** Custom inline styles to apply to toolbar */
  customStyles?: Record<string, string>;
}

export interface FormDesignerOptions {
  enableGrid?: boolean;
  gridSize?: number;
  enableSnapping?: boolean;
  snapTolerance?: number;
  enableMultiSelect?: boolean;
  enableKeyboardShortcuts?: boolean;
  allowedElements?: FormElementType[];
  /** Elements that should be disabled (grayed out and non-interactive) */
  disabledElements?: FormElementType[];
  readOnly?: boolean;
  /**
   * AN-1801 — constrained "signing placement" mode. When true, the palette is
   * empty (no add) and the pre-seeded element(s) cannot be deleted, so the host
   * can present exactly one non-deletable, move/resize-only signature box.
   * Pair with `allowedElements: []` to hide the palette.
   */
  signingPlacement?: boolean;
  /**
   * AN-1851 — minimum height in PDF points for signature stamp boxes, for NEW placements.
   * Defaults to 40pt (`MIN_STAMP_HEIGHT_PT`) — a product floor set below the legibility
   * anchor: the stamp's name reaches the ~7pt print floor only at 45pt, and renders at 6.3pt
   * here. See `size-constraints.ts` for the trade.
   * A box already stored below the minimum keeps its own height as its floor; nothing here
   * can reach stored geometry. Does not apply to initials, which carry no stamp content.
   */
  minStampHeight?: number;
  /**
   * AN-1851 — minimum width for signature stamp boxes, as a multiple of the stamp's root
   * value (`root = boxHeight / 7`) — **em, not points**. Defaults to 9.6
   * (`MIN_STAMP_WIDTH_EM`), measured by the backend with the server's own font metrics and
   * expressed in em so it survives a font change and cannot drift between repositories.
   * Because the root derives from box height, the resulting minimum width is a function of
   * the box's own height: 54.9pt at the 40pt height clamp.
   */
  minStampWidthEm?: number;
  /** Toolbar configuration options */
  toolbar?: ToolbarOptions;
  /** List of signers who can own form elements */
  signers?: Signer[];
  /** ID of the currently active signer */
  activeSignerId?: string;
  /** Layout mode for PDF display */
  layoutMode?: 'boxed' | 'full-page';
  /** Toolbar positioning mode */
  toolbarPosition?: 'fixed' | 'sticky' | 'external';
  /** Custom CSS class for container styling */
  containerClass?: string;
  /** Locale for UI text (defaults to 'en') */
  locale?: 'en' | 'tr' | 'nl';
}
